import type { GraphQLTaggedNode } from 'relay-runtime';
import { graphql } from 'relay-runtime';

/**
 * V2 of the popular feed query — uses aiProperty directly on PopularFeedItem
 * instead of content.aiProperty (nested under Confluence monolith).
 *
 * This enables AGG to resolve AI snippets in parallel with the Confluence content
 * hop (HOP2a || HOP2b), reducing Popular Feed latency from ~1650ms to ~850ms P95.
 *
 * Key difference from V1:
 * - V1: nodes { content { aiProperty { ... } } }  <- serial: waits for Confluence hop
 * - V2: nodes { aiProperty { ... } content { ... } }  <- parallel: both fire simultaneously
 *
 * Used by S7 experiment gate (confluence_ai_snippets_popular_feed) to toggle
 * between old and new path. The data mapping layer in popularFeedTypes.ts normalises
 * the shape so PopularFeedCard receives the same interface regardless of path.
 *
 * Blocked by: S4 (graphql-central-schema) + S5 (cc-graphql) must be deployed before
 * this query can be tested end-to-end. Gate remains OFF until S5 is verified in staging.
 */
export const PopularFeedWithTimeGranularityStorageSnippetsV2Query: GraphQLTaggedNode = graphql`
	query PopularFeedWithTimeGranularityStorageSnippetsV2Query(
		$first: Int
		$after: String
		$timeGranularity: String = "default"
		$isRovoLLMEnabled: Boolean = false
	) {
		popularFeed(first: $first, after: $after, timeGranularity: $timeGranularity) {
			nodes {
				aiProperty(entryPoint: "CONFLUENCE_HOME_POPULAR_FEED", objectType: snippet_v2)
					@include(if: $isRovoLLMEnabled) {
					errorMessage
					createdAt
					objectData
				}
				content {
					title
					id
					type
					subType
					emoji: properties(key: "emoji-title-published") {
						nodes {
							id
							value
						}
					}
					contentProperties {
						latest {
							generatedBy
						}
					}
					excerpt(length: 350)
					labels {
						nodes {
							name
						}
					}
					links {
						webui
					}
					space {
						alias
						id
						key
						name
					}
					history {
						createdDate
						createdBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
						ownedBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
						}
					}
					metadata {
						lastModifiedDate
						comments {
							commentsCount
						}
						currentuser {
							favourited {
								isFavourite
							}
						}
					}
					version {
						friendlyWhen
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;
